import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";
import { generateHashKey } from "./hashUtils";
import moment from "moment";
import "moment/locale/fr";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { fr } from "date-fns/locale/fr";
import { enGB } from "date-fns/locale/en-GB";

// Register date-fns locales for react-datepicker
registerLocale("fr", fr);
registerLocale("en-GB", enGB);

// Supported languages
export const SUPPORTED_LANGUAGES = {
    "en-GB": { name: "en-GB", nativeName: "English", flag: "🇬🇧" },
    fr: { name: "fr-FR", nativeName: "Français", flag: "🇫🇷" },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// TEMPORARY (this release only): multilingual support ships hidden.
// Forces English everywhere and hides the language switcher.
// Revert by setting this back to `true` when French officially launches.
export const MULTILINGUAL_ENABLED = false;

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguage = "en-GB";

// Fallback language
export const FALLBACK_LANGUAGE: SupportedLanguage = "en-GB";

/**
 * Custom post-processor that converts English text keys to hash keys
 * This allows using t("Search Flights") in code while JSON has { "k_abc123": "..." }
 */
const hashKeyProcessor = {
    type: "postProcessor" as const,
    name: "hashKeyProcessor",
    process(value: string, key: string | string[], options: Record<string, unknown>, translator: { exists: (k: string) => boolean; translate: (k: string, opts?: Record<string, unknown>) => string }) {
        // If value was found (not equal to key), return it
        if (value !== key && value !== (Array.isArray(key) ? key[0] : key)) {
            return value;
        }

        // Try looking up by hash key
        const textKey = Array.isArray(key) ? key[0] : key;
        const hashKey = generateHashKey(textKey);

        if (translator.exists(hashKey)) {
            return translator.translate(hashKey, options);
        }

        // Return original value (the English text) as fallback
        return value;
    },
};

i18n
    // Load translations from /public/locales
    .use(HttpBackend)
    // Detect user language
    .use(LanguageDetector)
    // Pass i18n instance to react-i18next
    .use(initReactI18next)
    // Add custom hash key processor
    .use(hashKeyProcessor)
    // Initialize i18next
    .init({
        // Debug mode (disable in production)
        debug: import.meta.env.DEV,

        // Default and fallback language
        lng: DEFAULT_LANGUAGE,
        fallbackLng: FALLBACK_LANGUAGE,

        // Supported languages
        supportedLngs: Object.keys(SUPPORTED_LANGUAGES),

        // Namespace configuration
        ns: ["translation"],
        defaultNS: "translation",

        // Enable the hash key post processor
        postProcess: ["hashKeyProcessor"],

        // Disable separators so colons/dots in translation keys are treated literally
        nsSeparator: false,
        keySeparator: false,

        // Interpolation settings
        interpolation: {
            escapeValue: false, // React already escapes values
        },

        // Backend configuration for loading translations
        // Files were renamed from fr/translation.json → fr-FR/fr.json,
        // so map the "fr" language code to its new path.
        backend: {
            loadPath: (lngs: string[], _namespaces: string[]) => {
                const lng = lngs[0];
                if (lng === "fr") return "/locales/fr-FR/fr.json";
                return "/locales/en-GB/en.json";
            },
        },

        // Language detection configuration
        detection: {
            // Order of language detection methods
            order: ["localStorage", "navigator", "htmlTag"],
            // Cache user language preference
            caches: ["localStorage"],
            // LocalStorage key for language preference
            lookupLocalStorage: "i18nextLng",
        },

        // React configuration
        react: {
            useSuspense: true,
        },
    });

// Update HTML lang attribute and sync moment/datepicker locale when language changes
i18n.on("languageChanged", (lng) => {
    document.documentElement.setAttribute("lang", lng);
    moment.locale(lng);
    setDefaultLocale(lng);
});

export default i18n;
